"use client";

import Button from "@/components/Button";
import { TreatmentNameToInitialQuestionnaireIDMapping } from "@/constants/Mappings";
import { Treatment } from "@/constants/treatments";
import { useIsMobile } from "@/hooks/use-mobile";
import { PillBGMapping, PillMapping } from "@/utils/discover-mapping";
import {
    ArrowLongLeftIcon,
    ArrowLongRightIcon,
    ArrowRightIcon,
} from "@heroicons/react/24/solid";
import { Route } from "next";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";

const MedicationOptions = ({
    treatments,
    conditionName,
}: {
    treatments: Treatment[];
    conditionName?: string;
}) => {
    const isMobile = useIsMobile();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [currentSection, setCurrentSection] = useState(1);
    const [itemsPerSection, setItemsPerSection] = useState(4);
    const [totalSections, setTotalSections] = useState(
        Math.ceil(treatments.length / itemsPerSection),
    );

    useEffect(() => {
        const updateLayout = () => {
            if (!isMobile && scrollContainerRef.current) {
                const containerWidth = scrollContainerRef.current.offsetWidth;
                const cardWidth = 288; // Adjust to match actual card width
                const newItemsPerSection = Math.floor(
                    containerWidth / cardWidth,
                );

                setItemsPerSection(newItemsPerSection || 1);
                setTotalSections(
                    Math.ceil(treatments.length / newItemsPerSection),
                );
            }
        };

        updateLayout();
        window.addEventListener("resize", updateLayout);

        return () => window.removeEventListener("resize", updateLayout);
    }, [treatments.length]);

    const scroll = (direction: string) => {
        if (scrollContainerRef.current) {
            // Calculate scroll amount based on card width (288px) + gap (24px)
            const cardWidth = 288;
            const cardGap = 24;
            const scrollAmount = (cardWidth + cardGap) * itemsPerSection;

            const targetScroll =
                direction === "left" ? -scrollAmount : scrollAmount;

            if (direction === "left" && currentSection > 1) {
                setCurrentSection(currentSection - 1);
            } else if (
                direction === "right" &&
                currentSection < totalSections
            ) {
                setCurrentSection(currentSection + 1);
            }

            scrollContainerRef.current.scrollBy({
                left: targetScroll,
                behavior: "smooth",
            });
        }
    };

    const shouldShowNavigation = totalSections > 1;
    const cardsContainerClass = `flex gap-6 overflow-x-hidden scroll-smooth p-2 px-6 ${
        !shouldShowNavigation ? "justify-center" : ""
    }`;

    return (
        <div className="my-8 rounded-2xl">
            {/* Title Section */}
            <div className={`text-center ${treatments.length > 0 && "mb-8"}`}>
                <h2 className="text-4xl font-semibold text-[#333333] dark:text-white font-satoshi uppercase">
                    Explore our range of {conditionName} treatments
                </h2>
                <p className="text-xl text-[#333333] dark:text-gray-300 font-space mt-4">
                    RTHM provides prescription to a variety of medications for
                    Long COVID, ME/CFS, POTS and MCAS
                </p>
            </div>

            {isMobile ? (
                // Mobile Structure
                <div className="flex flex-col gap-4">
                    {treatments.map((treatment) => {
                        const initialQuestionnaireId =
                            TreatmentNameToInitialQuestionnaireIDMapping[
                                treatment.name
                            ];
                        const url =
                            "/treatments/" +
                            treatment.name
                                .toLocaleLowerCase()
                                .replace(/ /g, "-");
                        return (
                            <Link
                                key={treatment.id}
                                href={url as Route}
                                onClick={() => {
                                    posthog?.capture("treatment_card_clicked", {
                                        treatmentName: treatment.name,
                                        conditionName,
                                    });
                                }}
                                className="flex bg-card rounded-xl shadow-md mx-1 flex-row w-full"
                            >
                                {/* Image Section */}
                                <div
                                    className={`w-20 flex items-center justify-center rounded-l-lg shrink-0 ${PillBGMapping[treatment.id] ?? "bg-[#cbd9ee]"}`}
                                >
                                    <div
                                        className={`rounded-l-lg flex items-center justify-center p-2`}
                                    >
                                        {PillMapping[treatment.id]?.(48)}
                                    </div>
                                </div>

                                {/* Content Section */}
                                <div className="p-4 flex flex-col gap-2 grow">
                                    <div className="flex justify-between items-center">
                                        <div className="text-lg font-medium text-[#1a5b77] dark:text-blue-400 font-satoshi flex-row flex justify-left items-center leading-[16.80px]">
                                            {treatment.name}
                                            {!initialQuestionnaireId && (
                                                <span className="ml-1 text-[#1a5b77]/70 dark:text-blue-300 text-sm font-normal italic font-satoshi leading-tight">
                                                    (Coming Soon)
                                                </span>
                                            )}
                                        </div>
                                        <ArrowRightIcon className="h-6 w-6 text-[#1a5b77] dark:text-blue-400" />
                                    </div>
                                    <p className="text-base text-[#333333] dark:text-gray-300 font-light font-space mt-1 leading-tight">
                                        {treatment.card_description}
                                    </p>
                                </div>
                            </Link>
                        );
                    })}
                    {/* Add "Don't know where to start?" card */}
                    <div className="flex bg-card rounded-xl shadow-md ml-1">
                        {/* Image Section */}
                        <div className="w-20 flex items-center justify-center rounded-l-lg shrink-0 bg-[#DFE4EA]">
                            <div className="bg-[#DFE4EA] rounded-l-lg flex items-center justify-center p-2">
                                <Image
                                    src={"/images/pills/PillGroup.png"}
                                    alt="Pills"
                                    width={0}
                                    height={0}
                                    sizes="100vw"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                    }}
                                />
                            </div>
                        </div>

                        {/* Content Section */}
                        <Link
                            href="/discover"
                            onClick={() => {
                                posthog?.capture(
                                    "discover_my_treatments_clicked",
                                    {
                                        conditionName,
                                    },
                                );
                            }}
                        >
                            <div className="p-4 flex flex-col gap-2 grow">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-[#1a5b77] dark:text-blue-400 font-satoshi leading-[16.80px]">
                                        Don&apos;t know where to start?
                                    </h3>
                                    <ArrowRightIcon className="h-6 w-6 text-[#1a5b77] dark:text-blue-400" />
                                </div>
                                <p className="text-base text-[#1a5b77] dark:text-white font-light font-space mt-1 leading-tight">
                                    We can help! Our treatment discovery tool
                                    takes in your symptoms and their severity
                                    levels and provides a tailored list of
                                    medications that may support your journey
                                    back to health.
                                </p>
                                <p className="text-base text-[#1a5b77] dark:text-white font-light font-space mt-1 leading-tight">
                                    Get personalized suggestions based on your
                                    specific symptoms and conditions and save
                                    time sifting through countless research
                                    papers.
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            ) : (
                // Desktop Structure
                <>
                    {shouldShowNavigation && (
                        <div className="flex items-center justify-between mx-10">
                            {/* Progress Bar */}
                            <div className="flex items-center gap-4">
                                {Array.from(
                                    { length: totalSections },
                                    (_, i) => (
                                        <div
                                            key={i}
                                            className={`h-2 w-10 rounded-md ${
                                                currentSection === i + 1
                                                    ? "bg-[#1a5b77]"
                                                    : "bg-gray-200"
                                            }`}
                                        ></div>
                                    ),
                                )}
                            </div>
                            {/* Arrows */}
                            <div className="flex gap-2">
                                <ArrowLongLeftIcon
                                    onClick={() => scroll("left")}
                                    className="h-8 w-8 text-[#1a5b77] cursor-pointer"
                                />
                                <ArrowLongRightIcon
                                    onClick={() => scroll("right")}
                                    className="h-8 w-8 text-[#1a5b77] cursor-pointer"
                                />
                            </div>
                        </div>
                    )}
                    <div
                        ref={scrollContainerRef}
                        className={cardsContainerClass}
                    >
                        {treatments.map((treatment) => {
                            const initialQuestionnaireId =
                                TreatmentNameToInitialQuestionnaireIDMapping[
                                    treatment.name
                                ];
                            const url =
                                "/treatments/" +
                                treatment.name
                                    .toLocaleLowerCase()
                                    .replace(/ /g, "-");

                            return (
                                <Link
                                    href={url as Route}
                                    onClick={() => {
                                        posthog?.capture(
                                            "treatment_card_clicked",
                                            {
                                                treatmentName: treatment.name,
                                                conditionName,
                                            },
                                        );
                                    }}
                                    key={treatment.id}
                                    className="shrink-0 w-72 bg-card rounded-2xl shadow-md flex flex-col justify-between transition-all duration-300 ease-out hover:translate-y-[-7px] hover:shadow-lg overflow-hidden"
                                >
                                    {/* Image Section */}
                                    <div
                                        className={`h-72 rounded-t-2xl ${PillBGMapping[treatment.id] ?? "bg-[#cbd9ee]"} flex items-center justify-center`}
                                    >
                                        {PillMapping[treatment.id]?.(120)}
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-6 flex flex-col h-full">
                                        <div className="grow">
                                            <h3 className="text-2xl font-medium text-[#1a5b77] dark:text-blue-400 font-satoshi flex-row flex justify-left items-center">
                                                {treatment.name}
                                                {!initialQuestionnaireId && (
                                                    <span className="ml-2 text-[#1a5b77] dark:text-blue-300 text-sm font-normal italic font-satoshi leading-tight">
                                                        (Coming Soon)
                                                    </span>
                                                )}
                                            </h3>
                                            <p className="text-base text-[#333333] dark:text-gray-300 font-light font-space mt-2">
                                                {treatment.card_description}
                                            </p>
                                        </div>
                                        <Button
                                            text="Learn More"
                                            id={`learn-more-${treatment.name}`}
                                            type="filled"
                                            className="w-full uppercase"
                                            height="h-12"
                                        />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                    <div
                        className={`${treatments.length > 0 && "mt-16"} bg-card rounded-2xl mx-6 lg:mx-32 p-4`}
                    >
                        <div className="flex flex-row items-center justify-between">
                            {/* Left side - Image */}
                            <div className="w-1/2 mr-8">
                                <Image
                                    src={"/images/pills/PillGroup.png"}
                                    alt="Pills"
                                    width={0}
                                    height={0}
                                    sizes="100vw"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                    }}
                                />
                            </div>

                            {/* Right side - Content */}
                            <div className="w-1/2 flex flex-col gap-5">
                                <div className="flex flex-col gap-5">
                                    <h2 className="text-[44px] font-semibold font-satoshi text-[#333333] dark:text-white uppercase leading-[57.20px]">
                                        Don&apos;t know
                                        <br />
                                        where to start?
                                    </h2>

                                    <div className="flex flex-col gap-4">
                                        <p className="text-lg font-light font-space text-[#1a5b77] dark:text-white leading-[25.20px]">
                                            We can help! Our treatment discovery
                                            tool takes in your symptoms and
                                            their severity levels and provides a
                                            tailored list of medications that
                                            may support your journey back to
                                            health.
                                        </p>
                                        <p className="text-lg font-light font-space text-[#1a5b77] dark:text-white leading-[25.20px]">
                                            Get personalized suggestions based
                                            on your specific symptoms and
                                            conditions and save time sifting
                                            through countless research papers.
                                        </p>
                                    </div>
                                </div>
                                <Link
                                    href="/discover"
                                    onClick={() => {
                                        posthog?.capture(
                                            "discover_my_treatments_clicked",
                                            {
                                                conditionName,
                                            },
                                        );
                                    }}
                                >
                                    <Button
                                        text="Discover My Treatments"
                                        type="filled"
                                        id="discover-treatments"
                                        height="h-14"
                                        className="uppercase px-8"
                                    />
                                </Link>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MedicationOptions;
